import { useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ModalCompAdvanceUpdate } from "../../../components";
import Message from "../../../components/MessageModal";
import {
  useGetArchivedMembersQuery,
  useSearchMembersQuery,
  useDownloadMemberCSVMutation,
  useDownloadMemberPDFMutation,
} from "../../../store/admin/memberManagement/memberSlice";
import StaffMemberTable from "../modules/StaffMemberTable";
import { ViewMembersHeader } from "../../../utils/TableHeader";
import useMemberRegAction from "./useMemberRegAction";
import { toast } from "react-hot-toast";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default function ArchivedMembers() {
  const navigate = useNavigate();
  const location = useLocation();
  const [getModalName, setModalName] = useState();
  const [memberId, setMemeberId] = useState();
  const [searchTerm, setSearchTerm] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const { unarchiveMember, activateMember } = useMemberRegAction();
  const [downloadCSV, { isLoading: isDownloadingCSV }] = useDownloadMemberCSVMutation();
  const [downloadPDF, { isLoading: isDownloadingPDF }] = useDownloadMemberPDFMutation();

  const handleUnarchive = async (memberId) => {
    setModalName();
    if (!memberId) return;
    await unarchiveMember(memberId);
    refetchArchivedMembers();
  };

  const handleActivate = async (memberId) => {
    setModalName();
    if (!memberId) return;
    await activateMember(memberId);
    refetchArchivedMembers();
  };

  const handleDownloadCSV = async () => {
    try {
      const toastId = toast.loading("Downloading CSV...");
      const blob = await downloadCSV({
        search: searchTerm,
        page: pageNumber,
      }).unwrap();

      if (!blob || blob.size === 0) {
        toast.error("CSV file is empty. Please try again.", { id: toastId });
        return;
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `archived-member-list-${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success("CSV downloaded successfully!", { id: toastId });
    } catch (error) {
      toast.error(error?.data?.message || "Failed to download CSV. Please try again.");
      console.error("CSV download error:", error);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const toastId = toast.loading("Generating PDF...");
      
      const membersToExport = searchTerm ? searchedMembers : allMembers;
      
      if (!membersToExport || membersToExport.length === 0) {
        toast.error("No archived members to export.", { id: toastId });
        return;
      }

      const doc = new jsPDF();
      
      doc.setFontSize(18);
      doc.text("Archived Member List Report", 14, 20);
      
      doc.setFontSize(10);
      doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 28);
      
      if (searchTerm) {
        doc.text(`Search term: ${searchTerm}`, 14, 34);
      }

      const tableData = membersToExport.map((member, index) => [
        index + 1,
        member.member_no || "N/A",
        member.first_name || "",
        member.last_name || "",
        member.email || "",
        member.phone || "",
        member.gender || "N/A",
        member.date_of_birth 
          ? new Date(member.date_of_birth).toLocaleDateString() 
          : "N/A",
        member.is_suspended 
          ? "Suspended" 
          : member.is_active 
            ? "Active" 
            : "Pending",
        member.branch || "N/A",
        member.created_at 
          ? new Date(member.created_at).toLocaleDateString() 
          : "N/A",
      ]);

      autoTable(doc, {
        startY: searchTerm ? 40 : 34,
        head: [
          [
            "S/N",
            "Membership No",
            "First Name",
            "Last Name",
            "Email",
            "Phone",
            "Gender",
            "Date of Birth",
            "Status",
            "Branch",
            "Date Created",
          ],
        ],
        body: tableData,
        theme: "striped",
        headStyles: {
          fillColor: [56, 56, 56],
          textColor: [255, 255, 255],
          fontStyle: "bold",
        },
        styles: {
          fontSize: 8,
          cellPadding: 2,
        },
        columnStyles: {
          0: { cellWidth: 15 },
          1: { cellWidth: 30 },
          2: { cellWidth: 30 },
          3: { cellWidth: 30 },
          4: { cellWidth: 40 },
          5: { cellWidth: 30 },
          6: { cellWidth: 20 },
          7: { cellWidth: 30 },
          8: { cellWidth: 25 },
          9: { cellWidth: 35 },
          10: { cellWidth: 30 },
        },
        margin: { top: 40 },
      });

      const fileName = `archived-member-list-${new Date().toISOString().split("T")[0]}.pdf`;
      doc.save(fileName);

      toast.success("PDF downloaded successfully!", { id: toastId });
    } catch (error) {
      toast.error("Failed to generate PDF. Please try again.");
      console.error("PDF generation error:", error);
    }
  };

  const { data: searchResult = {}, isFetching: isSearching } =
    useSearchMembersQuery(
      {
        page: pageNumber,
        search: searchTerm,
      },
      {
        skip: !searchTerm || searchTerm.trim() === "",
      }
    );

  const {
    data: archivedMembersResult = {},
    isFetching: isFetchingData,
    error,
    refetch: refetchArchivedMembers,
  } = useGetArchivedMembersQuery(pageNumber);


  const { allMembers, pagination } = useMemo(() => {
    const fetchedMembers = archivedMembersResult?.members || [];
    const pagination = archivedMembersResult?.pagination || {};
    const perPage = pagination.perPage || 20;
    const formattedMembers = fetchedMembers.map((item, index) => {
      return {
        ...item,
        name: item.first_name + " " + item.last_name,
        phone: item.phone,
        email: item.email,
        status: item.status,
        branch: item.branch?.name || "N/A",
        photo: item.photo,
        serialNumber: index + 1 + (pageNumber - 1) * perPage,
        id: item.id,
        is_active: item.is_activated,
        is_suspended: item.is_suspended || !item.is_activated,
        is_archived: item.is_archived,
      };
    });
    return { allMembers: formattedMembers, pagination };
  }, [archivedMembersResult, pageNumber]);

  const onSearch = (value) => {
    setPageNumber(1);
    setSearchTerm(value);
  };

  const { searchedMembers, searchedPagination } = useMemo(() => {
    const fetchedMembers = searchResult?.members || [];
    const pagination = searchResult?.pagination || {};
    const perPage = pagination.perPage || 20;
    const formattedMembers = fetchedMembers
      .filter((item) => item.is_archived === 1)
      .map((item, index) => {
        return {
          ...item,
          name: item.first_name + " " + item.last_name,
          phone: item.phone,
          email: item.email,
          status: item.status,
          branch: item.branch?.name || "N/A",
          photo: item.photo,
          serialNumber: index + 1 + (pageNumber - 1) * perPage,
          id: item.id,
          is_active: item.is_activated,
          is_suspended: item.is_suspended || !item.is_activated,
        };
      });
    return {
      searchedMembers: formattedMembers,
      searchedPagination: pagination,
    };
  }, [searchResult, pageNumber]);

  if (error && error.status === 401) {
    return navigate("/login");
  }

  const getTableDropdown = (row) => {
    const dropdown = [
      {
        name: "View profile",
        action: (data) => {
          navigate(`/super_admin/member_management/members/${data?.id}`);
        },
      },
      {
        name: "Activate Member",
        action: (data) => {
          setMemeberId(data?.id);
          setModalName("Activate Member");
        },
      },
      {
        name: "Unarchive Member",
        action: (data) => {
          setMemeberId(data?.id);
          setModalName("Unarchive Member");
        },
      },
    ];
    return dropdown;
  };

  const useModal = [
    {
      name: "Activate Member",
      component: (
        <Message
          title={"Activate Member"}
          desc={"Do you want to activate this member account? This will also unarchive the member if archived."}
          onBack={() => setModalName()}
          onProceed={() => {
            setModalName();
            handleActivate(memberId);
          }}
        />
      ),
    },
    {
      name: "Unarchive Member",
      component: (
        <Message
          title={"Unarchive Member"}
          desc={"Do you want to unarchive this member account?"}
          onBack={() => setModalName()}
          onProceed={() => {
            setModalName();
            handleUnarchive(memberId);
          }}
        />
      ),
    },
  ];

  return (
    <>
      <StaffMemberTable
        title={"Archived Members"}
        tableFor="members"
        tableData={searchTerm ? searchedMembers : allMembers}
        isFetchingData={isFetchingData || isSearching}
        tableColumns={ViewMembersHeader(getTableDropdown)}
        addNew={() => navigate("/super_admin/member_management")}
        pagination={searchTerm ? searchedPagination : pagination}
        onPageChange={setPageNumber}
        onSearch={onSearch}
        onDownloadCSV={handleDownloadCSV}
        onDownloadPDF={handleDownloadPDF}
        isDownloadingCSV={isDownloadingCSV}
        isDownloadingPDF={isDownloadingPDF}
        searchTerm={searchTerm}
        pageNumber={pageNumber}
      />
      <ModalCompAdvanceUpdate
        centered
        arrayComp={useModal}
        pageName={getModalName}
        handleClose={() => setModalName()}
      />
    </>
  );
}

